import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  Row,
  Col,
  Table,
  Input,
  Badge,
  Tooltip,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

import { getPlacedOrders, updateDeliveryDate } from "../service";
import "../styles/orders.css";

const Orders = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [placedFilter, setPlacedFilter] = useState([]);

  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [orders, setOrders] = useState([]);
  const [tooltipOpenId, setTooltipOpenId] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [modalOpen, setModalOpen] = useState(false); // NEW: modal state for delivery date update
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getPlacedOrders()
      .then((res) => setOrders(res))
      .catch((err) => console.error("Error fetching orders:", err));
  }, []);

  const STATUS_OPTIONS = [
    { label: "Approved", value: "approved" },
    { label: "Confirmed", value: "confirmed" },
    { label: "Placed", value: "placed" },
    { label: "Production", value: "production" },
    { label: "Delivered", value: "delivered" },
  ];

  const formatCurrency = (value) =>
    `₹${new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(value)}`;

  const formatDate = (raw) => {
    if (!raw) return "";
    return new Date(raw).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString("en-IN", {
      month: "short",
      year: "2-digit",
    });
  };

  const dateDiffBadge = (order, from, to) => {
    if (!from || !to) return null;
    const start = new Date(from);
    const end = new Date(to);
    const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
    const color =
      diffDays > 0 ? "danger" : diffDays < 0 ? "success" : "secondary";
    const sign = diffDays > 0 ? "+" : "";

    if (diffDays === 0) {
      return;
    }

    return (
      <>
        <Badge color={color} className="ms-2">
          {sign}
          {diffDays}d
        </Badge>
      </>
    );
  };

  const handleSaveDate = async () => {
    setIsLoading(true);

    try {
      await updateDeliveryDate({
        orderId: editingOrder.id,
        deliveryDate: newDate,
      });
      const updatedOrders = orders.map((o) => {
        if (o.id === editingOrder.id) {
          if (!o.planned_delivery_date) {
            return { ...o, planned_delivery_date: newDate };
          } else {
            return { ...o, revised_delivery_date: newDate };
          }
        }
        return o;
      });
      setOrders(updatedOrders);
      setModalOpen(false);
      setEditingOrder(null);
    } catch (error) {
      alert("Failed to update delivery date");
      console.error("Failed to update delivery date", error);
    }

    setIsLoading(false);
  };

  // NEW: Open modal for delivery date edit
  const openEditModal = (order) => {
    setEditingOrder(order);
    setNewDate(
      (order.revised_delivery_date || order.planned_delivery_date || "").split(
        "T"
      )[0]
    );
    setModalOpen(true);
  };

  const getDeliveryDateDisplay = (order) => {
    let iconClass = null;
    let displayDate = order.planned_delivery_date;
    let badge = null;

    if (order.delivered_at) {
      iconClass = "fa-solid fa-check-circle";
      displayDate = order.delivered_at;
      badge = dateDiffBadge(
        order,
        order.planned_delivery_date,
        order.delivered_at
      );
    } else if (order.revised_delivery_date) {
      displayDate = order.revised_delivery_date;
      badge = dateDiffBadge(
        order,
        order.planned_delivery_date,
        order.revised_delivery_date
      );
    }

    let color = "black";

    if (order.delivered_at) {
      color =
        order.delivered_at > order.planned_delivery_date ? "red" : "green";
    }
    // else if (!order.planned_delivery_date) {

    // }
    else if (order.revised_delivery_date) {
      color =
        order.revised_delivery_date > order.planned_delivery_date
          ? "orange"
          : "green";
    }

    const dateId = `tooltip-${order.id}`;

    return (
      <div className="d-flex order-date-cell position-relative">
        <span
          id={dateId}
          style={{ color }}
          className={`${!order.delivered_at && "pointer"}`}
          onClick={() =>
            !order.delivered_at &&
            order.status != "approved" &&
            order.status != "confirmed" &&
            openEditModal(order)
          }
        >
          {iconClass && <i className={`${iconClass} me-1`}></i>}
          {displayDate ? (
            formatDate(displayDate)
          ) : (
            <span className="text-muted">Not set</span>
          )}
          {badge}
          {order.planned_delivery_date &&
            order.revised_delivery_date &&
            !order.delivered_at && (
              <span className="text-muted small ms-2">revised</span>
            )}
          {order.planned_delivery_date &&
            !order.revised_delivery_date &&
            !order.delivered_at && (
              <span className="text-muted small ms-2">committed</span>
            )}
          {order.delivered_at && (
            <span className="text-muted small ms-2">delivered</span>
          )}
        </span>
        {order.planned_delivery_date &&
          (order.revised_delivery_date || order.delivered_at) && (
            <Tooltip
              target={dateId}
              isOpen={tooltipOpenId === dateId}
              toggle={() =>
                setTooltipOpenId(tooltipOpenId === dateId ? null : dateId)
              }
              placement="bottom"
            >
              Committed Date: {formatDate(order.planned_delivery_date, true)}
            </Tooltip>
          )}
      </div>
    );
  };

  const getStatusLabel = (status) => {
    const match = STATUS_OPTIONS.find((s) => s.value === status);
    return match ? match.label : status;
  };

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const statusOptions = [
    { label: "Approved", value: "approved" },
    { label: "Confirmed", value: "confirmed" },
    { label: "Placed", value: "placed" },
    { label: "Production", value: "production" },
    { label: "Delivered", value: "delivered" },
  ];

  const generatePlacedOptions = () => {
    const options = [];
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Last 6 months in reverse
    for (let i = 0; i < 6; i++) {
      const d = new Date(currentYear, currentMonth - i, 1);
      options.push({
        label: formatMonthYear(d),
        value: `m-${d.getFullYear()}-${d.getMonth() + 1}`,
      });
    }

    options.push({ label: "──────────", isDisabled: true });

    // Financial year quarters in reverse order
    const quarters = [
      { q: "Q4", months: [0, 1, 2] },
      { q: "Q3", months: [9, 10, 11] },
      { q: "Q2", months: [6, 7, 8] },
      { q: "Q1", months: [3, 4, 5] },
    ];

    const fy = now.getMonth() >= 3 ? now.getFullYear() + 1 : now.getFullYear();
    const allFYs = [fy - 1]; // FY 24–25

    allFYs.forEach((fyYear) => {
      quarters.forEach(({ q, months }) => {
        const baseYear = q === "Q4" ? fyYear - 1 : fyYear;
        const startMonth = Math.min(...months);
        const startDate = new Date(baseYear, startMonth, 1);
        if (startDate <= now) {
          options.push({
            label: `${q} ${String(fyYear - 1).slice(2)}-${String(fyYear).slice(
              2
            )}`,
            value: `q-${fyYear}-${q}`,
          });
        }
      });
    });

    options.push({ label: "──────────", isDisabled: true });

    options.push({
      label: `FY ${String(fy - 1).slice(2)}-${String(fy).slice(2)}`,
      value: `fy-${fy}`,
    });

    return options;
  };

  const isInPlacedFilter = (date) => {
    if (!placedFilter.length || !date) return true;
    const d = new Date(date);
    return placedFilter.some((filter) => {
      const [type, y, v] = filter.value.split("-");
      if (type === "m") {
        return d.getFullYear() === +y && d.getMonth() + 1 === +v;
      } else if (type === "q") {
        const fy = +y;
        const qMap = {
          Q1: [3, 4, 5],
          Q2: [6, 7, 8],
          Q3: [9, 10, 11],
          Q4: [0, 1, 2],
        };
        const months = qMap[v];
        const year = v === "Q4" ? fy - 1 : fy;
        return d.getFullYear() === year && months.includes(d.getMonth());
      } else if (type === "fy") {
        const fy = +y;
        const start = new Date(fy - 1, 3, 1);
        const end = new Date(fy, 2, 31);
        return d >= start && d <= end;
      }
      return true;
    });
  };

  const filteredOrders = orders.filter((o) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (!selectedStatus || o.status === selectedStatus) &&
      isInPlacedFilter(o.placed_date) &&
      (o.name.toLowerCase().includes(searchLower) ||
        o.identifier.toLowerCase().includes(searchLower) ||
        o.organization_name.toLowerCase().includes(searchLower) ||
        ("" + o.id).includes(searchLower) ||
        o.status.toLowerCase().includes(searchLower))
    );
  });

  const totalValue = filteredOrders.reduce((sum, o) => sum + (o.value || 0), 0);

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const aVal = a[sortKey] || "";
    const bVal = b[sortKey] || "";
    if (aVal < bVal) return sortAsc ? -1 : 1;
    if (aVal > bVal) return sortAsc ? 1 : -1;
    return 0;
  });

  const renderSortIcon = (key) => {
    if (key !== sortKey) return "";
    return sortAsc ? "▲" : "▼";
  };

  return (
    <Row className="border p-3 bg-white rounded">
      <div className="d-flex justify-content-between align-items-center mb-3 px-0">
        <div className="d-flex align-items-center d-flex gap-3">
          <div style={{ minWidth: 200 }}>
            <Select
              options={statusOptions}
              value={selectedStatus.value}
              onChange={(option) =>
                setSelectedStatus(option ? option.value : "")
              }
              isClearable
              placeholder="Status"
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <Select
              isMulti
              options={generatePlacedOptions()}
              value={placedFilter}
              onChange={setPlacedFilter}
              placeholder="Placed Date"
            />
          </div>
        </div>
        <input
          type="text"
          className="form-control w-25 me-3"
          placeholder="Search orders..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <Table
        bordered
        hover
        size="sm"
        className="align-middle mb-0"
        style={{ fontSize: "0.9rem" }}
      >
        <thead className="bg-secondary text-white">
          <tr>
            <th
              scope="col"
              onClick={() => handleSort("organization_name")}
              className="bg-secondary text-white pointer"
            >
              Customer {renderSortIcon("organization_name")}
            </th>
            <th
              scope="col"
              onClick={() => handleSort("name")}
              className="bg-secondary text-white pointer"
            >
              Order {renderSortIcon("name")}
            </th>
            <th
              scope="col"
              onClick={() => handleSort("value")}
              className="bg-secondary text-white pointer"
            >
              Value {renderSortIcon("value")}
              <span className="ms-2 fw-normal">
                {formatCurrency(totalValue)}
              </span>
            </th>
            <th
              scope="col"
              onClick={() => handleSort("placed_date")}
              className="bg-secondary text-white pointer"
            >
              Placed On {renderSortIcon("placed_date")}
            </th>
            <th
              scope="col"
              onClick={() => handleSort("status")}
              className="bg-secondary text-white pointer"
            >
              Status {renderSortIcon("status")}
            </th>
            <th
              scope="col"
              onClick={() => handleSort("delivered_at")}
              className="bg-secondary text-white pointer"
            >
              Delivery Date {renderSortIcon("delivered_at")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedOrders.map((order) => (
            <tr key={order.id}>
              <td>{order.organization_name}</td>
              <td>
                <a
                  href={`/projects/${order.project_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dark text-decoration-none"
                >
                  {order.name}
                </a>{" "}
                <span className="ms-2 text-muted small">
                  #{order.identifier}
                </span>
              </td>
              <td>{formatCurrency(order.value)}</td>
              <td>
                {order.placed_date ? (
                  formatDate(order.placed_date)
                ) : (
                  <span className="text-muted">Not Set</span>
                )}
              </td>
              <td>{getStatusLabel(order.status)}</td>
              <td>{getDeliveryDateDisplay(order)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalHeader toggle={() => setModalOpen(false)}>
          Update Delivery Date
        </ModalHeader>
        <ModalBody>
          {editingOrder && (
            <>
              <div className="card p-2 mb-4">
                <div className="d-flex gap-4">
                  <div>
                    <span className="text-muted small me-1">Customer:</span>
                    {editingOrder.organization_name}
                  </div>
                  <div>
                    <span className="text-muted small me-1">Order:</span>
                    {editingOrder.name}
                  </div>
                </div>
              </div>
              <input
                type="date"
                className="form-control w-25"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
              />
              <p className="text-muted mt-3">
                Note: Customer will be notified about this date change.
              </p>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" disabled={isLoading} onClick={handleSaveDate}>
            Update
          </Button>{" "}
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Row>
  );
};

export default Orders;
